import { Component, lazy, Suspense, type ErrorInfo, type ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { AuthProvider } from "@/hooks/useAuth";
import { CartProvider } from "@/hooks/cart/CartProvider";
import CartDrawer from "@/components/store/cart/CartDrawer";
import WhatsAppFloat from "@/components/store/WhatsAppFloat";
import Index from "./pages/Index.tsx";

// Lazy: everything except the homepage is split out of the initial bundle.
const BeforeAfter = lazy(() => import("./pages/BeforeAfter.tsx"));
const ProductDetail = lazy(() => import("./pages/store/ProductDetail.tsx"));
const Checkout = lazy(() => import("./pages/store/Checkout.tsx"));
const CheckoutSuccess = lazy(() => import("./pages/store/CheckoutSuccess.tsx"));
const CheckoutPending = lazy(() => import("./pages/store/CheckoutPending.tsx"));
const CheckoutFailure = lazy(() => import("./pages/store/CheckoutFailure.tsx"));
const About = lazy(() => import("./pages/store/About.tsx"));
const CategoryPage = lazy(() => import("./pages/store/CategoryPage.tsx"));
const BestSellers = lazy(() => import("./pages/store/BestSellers.tsx"));
const Wishlist = lazy(() => import("./pages/store/Wishlist.tsx"));
const Returns = lazy(() => import("./pages/store/Returns.tsx"));
const Faq = lazy(() => import("./pages/store/Faq.tsx"));
const PrivacyPolicy = lazy(() => import("./pages/store/PrivacyPolicy.tsx"));
const Login = lazy(() => import("./pages/Login.tsx"));
const AdminLogin = lazy(() => import("./pages/AdminLogin.tsx"));
const AdminForgotPassword = lazy(() => import("./pages/AdminForgotPassword.tsx"));
const AdminResetPassword = lazy(() => import("./pages/AdminResetPassword.tsx"));
const MfaSetup = lazy(() => import("./pages/MfaSetup.tsx"));
const MfaChallenge = lazy(() => import("./pages/MfaChallenge.tsx"));
const ProtectedRoute = lazy(() => import("./components/admin/ProtectedRoute.tsx"));
import MfaGate from "./components/auth/MfaGate";
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const AdminLayout = lazy(() => import("./components/admin/layout/AdminLayout.tsx"));
const Dashboard = lazy(() => import("./pages/admin/Dashboard.tsx"));
const Orders = lazy(() => import("./pages/admin/Orders.tsx"));
const OrderDetail = lazy(() => import("./pages/admin/OrderDetail.tsx"));
const MercadoPagoHistory = lazy(() => import("./pages/admin/MercadoPagoHistory.tsx"));
const ProductsList = lazy(() => import("./pages/admin/products/ProductsList.tsx"));
const ProductNew = lazy(() => import("./pages/admin/products/ProductNew.tsx"));
const ProductEdit = lazy(() => import("./pages/admin/products/ProductEdit.tsx"));
const Categories = lazy(() => import("./pages/admin/Categories.tsx"));
const Collections = lazy(() => import("./pages/admin/Collections.tsx"));
const Inventory = lazy(() => import("./pages/admin/Inventory.tsx"));
const Customers = lazy(() => import("./pages/admin/Customers.tsx"));
const CustomerDetail = lazy(() => import("./pages/admin/CustomerDetail.tsx"));
const Coupons = lazy(() => import("./pages/admin/Coupons.tsx"));
const Content = lazy(() => import("./pages/admin/Content.tsx"));
const Reports = lazy(() => import("./pages/admin/Reports.tsx"));
const Settings = lazy(() => import("./pages/admin/Settings.tsx"));
const RescueStories = lazy(() => import("./pages/admin/RescueStories.tsx"));
const ImportOrders = lazy(() => import("./pages/admin/ImportOrders.tsx"));
const ImportProducts = lazy(() => import("./pages/admin/ImportProducts.tsx"));
const ImportHistory = lazy(() => import("./pages/admin/ImportHistory.tsx"));
const BackupTools = lazy(() => import("./pages/admin/BackupTools.tsx"));
const Users = lazy(() => import("./pages/admin/Users.tsx"));
const EmailsLayout = lazy(() => import("./pages/admin/emails/EmailsLayout.tsx"));
const EmailsOverview = lazy(() => import("./pages/admin/emails/EmailsOverview.tsx"));
const EmailsTemplates = lazy(() => import("./pages/admin/emails/EmailsTemplates.tsx"));
const EmailsAutomations = lazy(() => import("./pages/admin/emails/EmailsAutomations.tsx"));
const EmailsLogs = lazy(() => import("./pages/admin/emails/EmailsLogs.tsx"));

const queryClient = new QueryClient();

const REFRESH_GUARD_KEY = "paws-purpose:fresh-reload";

const forceFreshReload = async (automatic = false) => {
  const lastRefresh = Number(window.sessionStorage.getItem(REFRESH_GUARD_KEY) ?? 0);
  if (automatic && Date.now() - lastRefresh < 30_000) return;

  window.sessionStorage.setItem(REFRESH_GUARD_KEY, String(Date.now()));

  try {
    const registrations = await navigator.serviceWorker?.getRegistrations();
    await Promise.all((registrations ?? []).map((registration) => registration.unregister()));

    if ("caches" in window) {
      const cacheNames = await window.caches.keys();
      await Promise.all(cacheNames.map((cacheName) => window.caches.delete(cacheName)));
    }
  } catch (error) {
    console.warn("[cache-recovery] não foi possível limpar todo o cache", error);
  }

  const freshUrl = new URL(window.location.href);
  freshUrl.searchParams.set("atualizacao", String(Date.now()));
  window.location.replace(freshUrl.toString());
};

const isStaleDeploymentError = (error: Error) =>
  /ChunkLoadError|Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i.test(
    `${error.name} ${error.message}`,
  );

const AppLoading = () => (
  <div className="flex min-h-screen items-center justify-center bg-background px-6 text-center text-sm text-muted-foreground">
    Carregando página segura...
  </div>
);

class AppErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("[AppErrorBoundary] página não carregou", error, errorInfo);
    if (isStaleDeploymentError(error)) void forceFreshReload(true);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-background px-6">
          <div className="w-full max-w-md rounded-xl border border-border bg-card p-6 text-center shadow-sm">
            <h1 className="font-display text-xl font-semibold text-foreground">Não foi possível carregar esta etapa</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Isso pode ocorrer após uma atualização do site. Atualize a página para continuar com segurança.
            </p>
            <Button
              type="button"
              className="mt-5"
              onClick={() => void forceFreshReload()}
            >
              Limpar e atualizar
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <CartProvider>
              <CartDrawer />
            <WhatsAppFloat />
              <AppErrorBoundary>
              <Suspense fallback={<AppLoading />}>
              <MfaGate>
              <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/antes-depois" element={<BeforeAfter />} />
              <Route path="/sobre" element={<About />} />
              <Route path="/categoria/:slug" element={<CategoryPage />} />
              <Route path="/mais-vendidos" element={<BestSellers />} />
              <Route path="/favoritos" element={<Wishlist />} />
              <Route path="/trocas-e-devolucoes" element={<Returns />} />
              <Route path="/perguntas-frequentes" element={<Faq />} />
              <Route path="/politica-de-privacidade" element={<PrivacyPolicy />} />
              <Route path="/login" element={<Login />} />
              <Route path="/mfa/setup" element={<MfaSetup />} />
              <Route path="/mfa/challenge" element={<MfaChallenge />} />
              <Route path="/produto/:slug" element={<ProductDetail />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/checkout/sucesso/:orderNumber" element={<CheckoutSuccess />} />
              <Route path="/checkout/pendente/:orderNumber" element={<CheckoutPending />} />
              <Route path="/checkout/falha/:orderNumber" element={<CheckoutFailure />} />
              <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
            <Route path="/admin/reset-password" element={<AdminResetPassword />} />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="orders" element={<Orders />} />
              <Route path="orders/:id" element={<OrderDetail />} />
              <Route path="mercado-pago" element={<MercadoPagoHistory />} />
              <Route path="products" element={<ProductsList />} />
              <Route path="products/new" element={<ProductNew />} />
              <Route path="products/:id/edit" element={<ProductEdit />} />
              <Route path="categories" element={<Categories />} />
              <Route path="collections" element={<Collections />} />
              <Route path="inventory" element={<Inventory />} />
              <Route path="customers" element={<Customers />} />
              <Route path="customers/:id" element={<CustomerDetail />} />
              <Route path="coupons" element={<Coupons />} />
              <Route path="content" element={<Content />} />
              <Route path="rescue-stories" element={<RescueStories />} />
              <Route path="reports" element={<Reports />} />
              <Route path="settings" element={<Settings />} />
              <Route path="import-orders" element={<ImportOrders />} />
              <Route path="import-products" element={<ImportProducts />} />
              <Route path="import-history" element={<ImportHistory />} />
              <Route path="backup-tools" element={<BackupTools />} />
              <Route path="users" element={<Users />} />
              <Route path="emails" element={<EmailsLayout />}>
                <Route index element={<EmailsOverview />} />
                <Route path="templates" element={<EmailsTemplates />} />
                <Route path="automations" element={<EmailsAutomations />} />
                <Route path="logs" element={<EmailsLogs />} />
              </Route>
            </Route>
            <Route path="*" element={<NotFound />} />
              </Routes>
              </MfaGate>
              </Suspense>
              </AppErrorBoundary>
            </CartProvider>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </HelmetProvider>
  </QueryClientProvider>
);

export default App;
